<template></template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue';

import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);
import ChartDataLabels from 'chartjs-plugin-datalabels';

export default {
    props: {
        'statistics' : { type: Object },
        'width' : {
            type: String,
            default: '718px'
        },
        'height' : {
            type: String,
            default: '274px'
        },

    },

    setup(props, context) {
       let chartInstance=null


        const printGraph = () => {
            console.log('🖼️ printGraph called with statistics:', {
                hasStatistics: !!props.statistics,
                width: props.width,
                height: props.height,
                statisticsKeys: props.statistics ? Object.keys(props.statistics) : []
            });
            
            // First, destroy any existing chart instance
            if (chartInstance) {
                console.log('🗑️ Destroying existing chart instance before creating new one');
                try {
                    chartInstance.destroy();
                    chartInstance = null;
                } catch (error) {
                    console.warn('⚠️ Error destroying chart:', error);
                    chartInstance = null;
                }
            }
            
            // Get the canvas element and ensure it's clean
            const canvas = document.getElementById('map-graph');
            if (!canvas) {
                console.error('❌ Canvas element not found');
                return;
            }
            
            // Ensure canvas is properly mounted in DOM
            if (!canvas.parentNode) {
                console.error('❌ Canvas not mounted in DOM');
                return;
            }
            
            // Clear any existing Chart.js instance from the canvas
            if (canvas.chartInstance) {
                console.log('🧹 Found existing chart instance on canvas, destroying it');
                try {
                    canvas.chartInstance.destroy();
                } catch (error) {
                    console.warn('⚠️ Error destroying canvas chart instance:', error);
                }
                delete canvas.chartInstance;
            }
            
            // Clear the canvas context
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            
            console.log('✅ Canvas cleared and ready for new chart');
            
            const labels = props.statistics.labels;

            // Check if data is empty or all values are null/undefined
            const hasValidData = props.statistics.data && 
                                props.statistics.data.length > 0 && 
                                props.statistics.data.some(value => value !== null && value !== undefined && value !== '');
            
            // Check if this is a lime chart
            const isLimeChart = props.statistics.chartType && (
                props.statistics.chartType === 'lime_req_6_5' || 
                props.statistics.chartType === 'lime_req_7'
            );

            // If no valid data exists, display a message instead of an empty chart
            if (!hasValidData) {
                const canvas = document.getElementById('map-graph');
                const ctx = canvas.getContext('2d');
                
                // Clear canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                // Set canvas background to white
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                // Draw border
                ctx.strokeStyle = '#e5e7eb';
                ctx.lineWidth = 1;
                ctx.strokeRect(0, 0, canvas.width, canvas.height);
                
                // Draw "No Lime Required" message
                ctx.fillStyle = '#6b7280';
                ctx.font = '16px Inter, Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                
                const message = isLimeChart ? 'No Lime Required' : 'No Data Available';
                ctx.fillText(message, canvas.width / 2, canvas.height / 2 - 10);
                
                // Add subtitle
                ctx.font = '12px Inter, Arial, sans-serif';
                ctx.fillStyle = '#9ca3af';
                ctx.fillText('All values are nill', canvas.width / 2, canvas.height / 2 + 15);
                
                return; // Exit early without creating chart
            }

            // Color code bars based on index ranges
            const getBarColors = () => {
                // Helper function to convert various color formats to RGB
                const convertToRGB = (color, indexLabel = '') => {
                    // Use the actual micronutrient color scheme from config
                    const micronutrientFallbacks = {
                        'index_1': 'rgb(220, 38, 38)',    // Very Low - Dark Red
                        'index_2': 'rgb(245, 158, 11)',   // Low - Orange
                        'index_3': 'rgb(34, 197, 94)',    // Normal - Green
                        'index_4': 'rgb(21, 128, 61)',    // High - Dark Green
                        'index_5': 'rgb(15, 78, 37)'      // Very High - Darker Green
                    };
                    
                    if (!color) {
                        return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                    }
                    
                    // If it's already a hex color, return as is
                    if (typeof color === 'string' && color.startsWith('#')) {
                        return color;
                    }
                    
                    // If it's a comma-separated RGB string (e.g., "220,38,38")
                    if (typeof color === 'string' && color.includes(',')) {
                        const rgb = color.split(',').map(c => parseInt(c.trim()));
                        if (rgb.length >= 3 && !rgb.some(isNaN)) {
                            return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
                        }
                    }
                    
                    // If it's RGBA format, convert to RGB
                    if (typeof color === 'string' && color.startsWith('rgba')) {
                        const values = color.match(/[\d.]+/g);
                        if (values && values.length >= 3) {
                            const r = Math.floor(values[0]);
                            const g = Math.floor(values[1]);
                            const b = Math.floor(values[2]);
                            
                            // Check if it's a white placeholder (all values 255 or close to white)
                            if (r >= 250 && g >= 250 && b >= 250) {
                                return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                            }
                            
                            return `rgb(${r}, ${g}, ${b})`;
                        }
                    }
                    
                    // If it's RGB format, check for white and use fallback if needed
                    if (typeof color === 'string' && color.startsWith('rgb(')) {
                        const values = color.match(/[\d.]+/g);
                        if (values && values.length >= 3) {
                            const r = parseInt(values[0]);
                            const g = parseInt(values[1]);
                            const b = parseInt(values[2]);
                            
                            // Check if it's a white placeholder
                            if (r >= 250 && g >= 250 && b >= 250) {
                                return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                            }
                        }
                        return color;
                    }
                    
                    // If it's an array format [r, g, b, a], use first 3 values
                    if (Array.isArray(color) && color.length >= 3) {
                        const r = Math.floor(color[0]);
                        const g = Math.floor(color[1]);
                        const b = Math.floor(color[2]);
                        
                        // Check if it's a white placeholder
                        if (r >= 250 && g >= 250 && b >= 250) {
                            return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                        }
                        
                        return `rgb(${r}, ${g}, ${b})`;
                    }
                    
                    // If nothing else worked, return fallback
                    return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                };

                return props.statistics.data.map((value, index) => {
                    // Check if this is a chart that should have color-coded bars (Lime, pH, or any chart with background indices)


                    const isPHChart = props.statistics.chartType && props.statistics.chartType === 'ph_water';
                    const isKChart = props.statistics.title && props.statistics.title.toLowerCase().includes('potassium');
                    
                    // For pH charts, use deviation-based colors
                    if (isPHChart) {
                        const targetPH = 6.5;
                        const actualValue = props.statistics.data[index];
                        
                        if (actualValue === null || actualValue === undefined) {
                            return 'rgb(156, 163, 175)'; // Gray for missing data
                        }
                        
                        const deviation = Math.abs(actualValue - targetPH);
                        
                        if (deviation <= 0.3) {
                            return 'rgb(34, 197, 94)';    // Green - excellent (±0.3)
                        } else if (deviation <= 0.6) {
                            return 'rgb(132, 204, 22)';   // Light green - good (±0.6)
                        } else if (deviation <= 1.0) {
                            return 'rgb(245, 158, 11)';   // Orange - needs attention (±1.0)
                        } else {
                            return 'rgb(239, 68, 68)';    // Red - needs correction (>1.0)
                        }
                    }
                    
                    // Charts sent with parcel colors (Lime and trace elements) colour each bar as the map does
                    if (props.statistics.parcelColors?.[index]) {
                        const parcelColor = props.statistics.parcelColors[index];
                        // Convert parcel color format to CSS string
                        if (Array.isArray(parcelColor) && parcelColor.length >= 3) {
                            return `rgba(${parcelColor[0]}, ${parcelColor[1]}, ${parcelColor[2]}, ${parcelColor[3] || 1})`;
                        }
                        return parcelColor; // Use as-is if already a string
                    }
                    
                    const shouldColorCode = props.statistics.background;
                    
                    if (shouldColorCode && props.statistics.index_1 > -1) {
                        let assignedColor = '';
                        let assignedIndex = '';
                        
                        // Define major nutrient detection logic here
                        const hasThreeIndices = props.statistics.index_1 > -1 &&
                                              props.statistics.index_2 > -1 &&
                                              props.statistics.index_3 > -1;

                        // Use the exact same logic as the original background color system
                        if (value <= props.statistics.index_1) {
                            assignedColor = convertToRGB(props.statistics.index_1_colour, 'index_1');
                            assignedIndex = 'Index 1';
                        } else if (value <= props.statistics.index_2) {
                            assignedColor = convertToRGB(props.statistics.index_2_colour, 'index_2');
                            assignedIndex = 'Index 2';
                        } else if (value <= props.statistics.index_3) {
                            assignedColor = convertToRGB(props.statistics.index_3_colour, 'index_3');
                            assignedIndex = 'Index 3';
                        } else if (props.statistics.index_4 > -1 && value <= props.statistics.index_4) {
                            assignedColor = convertToRGB(props.statistics.index_4_colour, 'index_4');
                            assignedIndex = 'Index 4';
                        } else if (props.statistics.index_5 > -1) {
                            assignedColor = convertToRGB(props.statistics.index_5_colour, 'index_5');
                            assignedIndex = 'Index 5';
                        } else {
                            // For major nutrients without Index 4, and trace elements graded from a
                            // lab's target (no Very High), treat values > index_3 as Index 4 (High)
                            if (hasThreeIndices && value > props.statistics.index_3) {
                                // Use the existing index_4_colour even if index_4 value is missing
                                assignedColor = convertToRGB(props.statistics.index_4_colour, 'index_4');
                                assignedIndex = 'Index 4 (calculated)';
                            } else if (props.statistics.index_4 > -1) {
                                assignedColor = convertToRGB(props.statistics.index_4_colour, 'index_4_fallback');
                                assignedIndex = 'Index 4 (fallback)';
                            } else {
                                assignedColor = convertToRGB(props.statistics.index_3_colour, 'index_3_fallback');
                                assignedIndex = 'Index 3 (fallback)';
                            }
                        }
                        
                        // Debug logging for K charts
                        if (isKChart) {
                            console.log(`K Chart - Bar ${index}: Value=${value}, Assigned=${assignedIndex}, Color=${assignedColor}`);
                            console.log(`Index thresholds: 1=${props.statistics.index_1}, 2=${props.statistics.index_2}, 3=${props.statistics.index_3}, 4=${props.statistics.index_4}`);
                            console.log(`Chart title: "${props.statistics.title}"`);
                        }
                        
                        return assignedColor;
                    } else {
                        // Default color for non-indexed charts
                        return '#2D5A3D';
                    }
                });
            };

            // Transform pH data to show deviations from target
            let chartData = props.statistics.data;
            let yAxisTitle = props.statistics.title;
            let yAxisMin = 0;
            let yAxisMax = props.statistics.maximum;
            let showCenterLine = false;
            
            if (props.statistics.chartType === 'ph_water') {
                // Calculate deviations from target pH
                const targetPH = 6.5; // Default to grassland target
                chartData = props.statistics.data.map(value => {
                    if (value === null || value === undefined) return 0;
                    return Number((value - targetPH).toFixed(2));
                });
                
                yAxisTitle = 'pH Deviation from Target (6.5)';
                // Set y-axis range to show deviations symmetrically
                const maxDeviation = Math.max(...chartData.map(Math.abs));
                const range = Math.min(1.2, Math.max(1.0, Math.ceil(maxDeviation * 10) / 10)); // Range ±1.0 to ±1.2
                yAxisMin = -range;
                yAxisMax = range;
                showCenterLine = true;
            } else {
                // Calculate dynamic y-axis max for all other charts
                const validData = chartData.filter(value => value !== null && value !== undefined && !isNaN(value));
                
                if (validData.length > 0) {
                    // Trace element charts always show the whole Normal band, whose top can be above every value
                    const dataMax = Math.max(...validData, props.statistics.micronutrient ? props.statistics.index_3 : 0);
                    
                    console.log(`Y-Axis Max Calculation for "${props.statistics.title}":`, {
                        dataMax,
                        step: props.statistics.step,
                        originalMax: props.statistics.maximum
                    });
                    
                    // Calculate clean step-based maximum without padding
                    if (props.statistics.step && props.statistics.step > 0) {
                        // Use provided step for lime charts
                        const stepsNeeded = Math.ceil(dataMax / props.statistics.step);
                        yAxisMax = stepsNeeded * props.statistics.step;
                        
                        console.log('Step-based calculation:', {
                            calculation: `Math.ceil(${dataMax} / ${props.statistics.step}) = ${stepsNeeded}`,
                            result: `${stepsNeeded} * ${props.statistics.step} = ${yAxisMax}`
                        });
                    } else {
                        // For charts without explicit steps, predict Chart.js's automatic step calculation
                        // and align yAxisMax to those predicted steps
                        const paddedMax = dataMax * 1.1; // 10% padding
                        
                        // Predict what Chart.js will choose as automatic step size
                        let predictedStep;
                        if (paddedMax <= 10) {
                            predictedStep = 1;
                        } else if (paddedMax <= 20) {
                            predictedStep = 2;
                        } else if (paddedMax <= 50) {
                            predictedStep = 5;
                        } else if (paddedMax <= 100) {
                            predictedStep = 10;
                        } else if (paddedMax <= 200) {
                            predictedStep = 20;
                        } else if (paddedMax <= 500) {
                            predictedStep = 50;
                        } else if (paddedMax <= 1000) {
                            predictedStep = 100;
                        } else {
                            predictedStep = Math.pow(10, Math.floor(Math.log10(paddedMax / 5)));
                        }
                        
                        // Align yAxisMax to predicted step intervals
                        yAxisMax = Math.ceil(paddedMax / predictedStep) * predictedStep;
                        

                    }
                    
                    
                }
            }

            const data = {
                labels: labels,
                datasets: [{
                    data: chartData,
                    backgroundColor: getBarColors(),
                    borderRadius: 3, // Minimal rounding for bars
                    borderWidth: 0,
                    maxBarThickness: 60, // Limit maximum bar width to 60px
                    categoryPercentage: 1.0, // Use full category width for spacing
                    barPercentage: 0.8 // Bars take up 80% of category, leaving 20% for spacing
                }]
            };

                const config = {
                    type: 'bar',
                    data: data,
                    options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            grid: {display: false},
                            ticks: {
                                font: {
                                    family: 'Inter, Arial, sans-serif',
                                    size: 11
                                }
                            }
                        },
                        y: {
                            grid: {
                                display: true,
                                color: 'rgba(0,0,0,0.1)',
                                lineWidth: 0.5
                            },
                            beginAtZero: props.statistics.chartType !== 'ph_water',
                            title: {
                                display: false,
                                text: yAxisTitle,
                                font: {
                                    family: 'Inter, Arial, sans-serif',
                                    size: 12,
                                    weight: '500'
                                }
                            },
                            min: yAxisMin,
                            max: yAxisMax,
                            ticks: {
                                stepSize: props.statistics.step,
                                font: {
                                    family: 'Inter, Arial, sans-serif',
                                    size: 10
                                },
                                callback: function(value, index, values) {
                                    // Only show values that are exact multiples of stepSize
                                    if (props.statistics.step && value % props.statistics.step !== 0) {
                                        return null; // Hide non-step values
                                    }
                                    
                                    // Check if this is a potassium chart
                                    const isKChart = props.statistics.title && props.statistics.title.toLowerCase().includes('potassium');
                                    
                                    if (isKChart) {
                                        // Show whole numbers only for potassium charts
                                        return Math.round(value);
                                    } else {
                                        // Always format to at most 2 decimal places for other charts
                                        return Number(value).toFixed(2).replace(/\.?0+$/, '');
                                    }
                                }
                            }
                        }
                    },
                    elements: {
                        bar: {
                            borderRadius: 3,
                            borderWidth: 0
                        }
                    },
                    legend: {
                        display: false
                    },
                    plugins: {
                        datalabels: {
                            display: false,
                            // anchor: 'end',
                            // align: 'end',
                            // labels: {
                            //     value: {
                            //         color: 'black',
                            //         size: 10
                            //     }
                            // }
                        },
                        legend: { display: false }
                    },
                    layout: {
                        padding: {
                            top: 20
                        }
                    }
                },
                plugins: [
                    ChartDataLabels,
                    {
                        id: 'reference_lines',
                        afterDraw: chart => {
                            const ctx = chart.canvas.getContext('2d');
                            var xAxis = chart.scales.x;
                            var yAxis = chart.scales.y;

                            if (props.statistics.background) {
                                // Helper function to convert various color formats to RGB for reference lines
                                const convertToRGB = (color, indexLabel = '') => {
                                    // Use the actual micronutrient color scheme from config
                                    const micronutrientFallbacks = {
                                        'ref_index_1': 'rgb(220, 38, 38)',    // Very Low - Dark Red
                                        'ref_index_2': 'rgb(245, 158, 11)',   // Low - Orange
                                        'ref_index_3': 'rgb(34, 197, 94)',    // Normal - Green
                                        'ref_index_4': 'rgb(21, 128, 61)',    // High - Dark Green
                                        'ref_index_5': 'rgb(15, 78, 37)'      // Very High - Darker Green
                                    };
                                    
                                    if (!color) {
                                        return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                                    }
                                    
                                    // If it's already a hex color, return as is
                                    if (typeof color === 'string' && color.startsWith('#')) {
                                        return color;
                                    }
                                    
                                    // If it's a comma-separated RGB string (e.g., "220,38,38")
                                    if (typeof color === 'string' && color.includes(',')) {
                                        const rgb = color.split(',').map(c => parseInt(c.trim()));
                                        if (rgb.length >= 3 && !rgb.some(isNaN)) {
                                            return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
                                        }
                                    }
                                    
                                    // If it's RGBA format, convert to RGB
                                    if (typeof color === 'string' && color.startsWith('rgba')) {
                                        const values = color.match(/[\d.]+/g);
                                        if (values && values.length >= 3) {
                                            const r = Math.floor(values[0]);
                                            const g = Math.floor(values[1]);
                                            const b = Math.floor(values[2]);
                                            
                                            // Check if it's a white placeholder (all values 255 or close to white)
                                            if (r >= 250 && g >= 250 && b >= 250) {
                                                return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                                            }
                                            
                                            return `rgb(${r}, ${g}, ${b})`;
                                        }
                                    }
                                    
                                    // If it's RGB format, check for white and use fallback if needed
                                    if (typeof color === 'string' && color.startsWith('rgb(')) {
                                        const values = color.match(/[\d.]+/g);
                                        if (values && values.length >= 3) {
                                            const r = parseInt(values[0]);
                                            const g = parseInt(values[1]);
                                            const b = parseInt(values[2]);
                                            
                                            // Check if it's a white placeholder
                                            if (r >= 250 && g >= 250 && b >= 250) {
                                                return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                                            }
                                        }
                                        return color;
                                    }
                                    
                                    // If it's an array format [r, g, b, a], use first 3 values
                                    if (Array.isArray(color) && color.length >= 3) {
                                        const r = Math.floor(color[0]);
                                        const g = Math.floor(color[1]);
                                        const b = Math.floor(color[2]);
                                        
                                        // Check if it's a white placeholder
                                        if (r >= 250 && g >= 250 && b >= 250) {
                                            return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                                        }
                                        
                                        return `rgb(${r}, ${g}, ${b})`;
                                    }
                                    
                                    // If nothing else worked, return fallback
                                    return micronutrientFallbacks[indexLabel] || '#2D5A3D';
                                };

                                // Determine if this is a major nutrient (P, K) or micronutrient,
                                // which the chart data flags
                                const hasThreeIndices = props.statistics.index_1 > -1 &&
                                                      props.statistics.index_2 > -1 &&
                                                      props.statistics.index_3 > -1;
                                const isMajorNutrient = hasThreeIndices && !props.statistics.micronutrient;


                                // Draw horizontal reference lines instead of colored backgrounds
                                const referenceLines = [
                                    { 
                                        value: props.statistics.index_1, 
                                        label: isMajorNutrient ? 'Index 1' : 'Very Low', 
                                        color: convertToRGB(props.statistics.index_1_colour, 'ref_index_1')
                                    },
                                    { 
                                        value: props.statistics.index_2, 
                                        label: isMajorNutrient ? 'Index 2' : 'Low', 
                                        color: convertToRGB(props.statistics.index_2_colour, 'ref_index_2')
                                    },
                                    { 
                                        value: props.statistics.index_3, 
                                        label: isMajorNutrient ? 'Index 3' : 'Normal', 
                                        color: convertToRGB(props.statistics.index_3_colour, 'ref_index_3')
                                    },
                                ];

                                // Add index 4 if it exists
                                if (props.statistics.index_4 > -1) {
                                    referenceLines.push({ 
                                        value: props.statistics.index_4, 
                                        label: isMajorNutrient ? 'Index 4' : 'High', 
                                        color: convertToRGB(props.statistics.index_4_colour, 'ref_index_4')
                                    });
                                } else if (isMajorNutrient) {
                                    // For major nutrients, we expect index 4 but might be missing
                                    //console.log('Warning: Major nutrient missing Index 4');
                                }

                                // Add index 5 if it exists (only for micronutrients)
                                if (props.statistics.index_5 > -1 && !isMajorNutrient) {
                                    referenceLines.push({ 
                                        value: props.statistics.index_5, 
                                        label: 'Very High', 
                                        color: convertToRGB(props.statistics.index_5_colour, 'ref_index_5')
                                    });
                                }

                                if (isMajorNutrient) {
                                    // Draw index lines for major nutrients
                                    referenceLines.forEach(line => {
                                        const yPos = yAxis.getPixelForValue(line.value);
                                        
                                        // Draw dashed line
                                        ctx.strokeStyle = line.color;
                                        ctx.lineWidth = 1;
                                        ctx.setLineDash([6, 3]);
                                        
                                        ctx.beginPath();
                                        ctx.moveTo(xAxis.left, yPos);
                                        ctx.lineTo(xAxis.right, yPos);
                                        ctx.stroke();
                                    });

                                    // Add labels in the middle of each index band
                                    ctx.setLineDash([]);
                                    ctx.font = '9px Inter, Arial, sans-serif';
                                    ctx.textAlign = 'right';

                                    // Index 1 band (0 to index_1)
                                    if (props.statistics.index_1 > -1) {
                                        const midY = yAxis.getPixelForValue(props.statistics.index_1 / 2);
                                        ctx.fillStyle = convertToRGB(props.statistics.index_1_colour, 'ref_index_1');
                                        ctx.fillText('Index 1', xAxis.right - 8, midY + 3);
                                    }

                                    // Index 2 band (index_1 to index_2)
                                    if (props.statistics.index_1 > -1 && props.statistics.index_2 > -1) {
                                        const midValue = (props.statistics.index_1 + props.statistics.index_2) / 2;
                                        const midY = yAxis.getPixelForValue(midValue);
                                        ctx.fillStyle = convertToRGB(props.statistics.index_2_colour, 'ref_index_2');
                                        ctx.fillText('Index 2', xAxis.right - 8, midY + 3);
                                    }

                                    // Index 3 band (index_2 to index_3)
                                    if (props.statistics.index_2 > -1 && props.statistics.index_3 > -1) {
                                        const midValue = (props.statistics.index_2 + props.statistics.index_3) / 2;
                                        const midY = yAxis.getPixelForValue(midValue);
                                        ctx.fillStyle = convertToRGB(props.statistics.index_3_colour, 'ref_index_3');
                                        ctx.fillText('Index 3', xAxis.right - 8, midY + 3);
                                    }

                                    // Index 4 band (index_3 to index_4 or top of chart)
                                    if (props.statistics.index_3 > -1) {
                                        let topValue;
                                        if (props.statistics.index_4 > -1) {
                                            topValue = props.statistics.index_4;
                                        } else {
                                            // If no index_4, use chart maximum for the band
                                            topValue = yAxis.max;
                                        }
                                        const midValue = (props.statistics.index_3 + topValue) / 2;
                                        const midY = yAxis.getPixelForValue(midValue);
                                        ctx.fillStyle = convertToRGB(props.statistics.index_4_colour, 'ref_index_4');
                                        ctx.fillText('Index 4', xAxis.right - 8, midY + 3);
                                    }
                                } else {
                                    // Draw range bands for micronutrients
                                    // Red line: Low to Very Low boundary (index_1)
                                    if (props.statistics.index_1 > -1) {
                                        const yPos = yAxis.getPixelForValue(props.statistics.index_1);
                                        ctx.strokeStyle = 'rgb(220, 38, 38)'; // Red
                                        ctx.lineWidth = 1.5;
                                        ctx.setLineDash([6, 3]);
                                        ctx.beginPath();
                                        ctx.moveTo(xAxis.left, yPos);
                                        ctx.lineTo(xAxis.right, yPos);
                                        ctx.stroke();
                                    }

                                    // Green lines: Normal range (index_2 to index_3)
                                    if (props.statistics.index_2 > -1) {
                                        const yPos = yAxis.getPixelForValue(props.statistics.index_2);
                                        ctx.strokeStyle = 'rgb(34, 197, 94)'; // Green
                                        ctx.lineWidth = 1.5;
                                        ctx.setLineDash([6, 3]);
                                        ctx.beginPath();
                                        ctx.moveTo(xAxis.left, yPos);
                                        ctx.lineTo(xAxis.right, yPos);
                                        ctx.stroke();
                                    }
                                    
                                    if (props.statistics.index_3 > -1) {
                                        const yPos = yAxis.getPixelForValue(props.statistics.index_3);
                                        ctx.strokeStyle = 'rgb(34, 197, 94)'; // Green
                                        ctx.lineWidth = 1.5;
                                        ctx.setLineDash([6, 3]);
                                        ctx.beginPath();
                                        ctx.moveTo(xAxis.left, yPos);
                                        ctx.lineTo(xAxis.right, yPos);
                                        ctx.stroke();
                                    }

                                    // Deep green line: High to Very High boundary (index_4)
                                    if (props.statistics.index_4 > -1) {
                                        const yPos = yAxis.getPixelForValue(props.statistics.index_4);
                                        ctx.strokeStyle = 'rgb(21, 128, 61)'; // Deep green
                                        ctx.lineWidth = 1.5;
                                        ctx.setLineDash([6, 3]);
                                        ctx.beginPath();
                                        ctx.moveTo(xAxis.left, yPos);
                                        ctx.lineTo(xAxis.right, yPos);
                                        ctx.stroke();
                                    }

                                    // Add labels in the middle of each band
                                    ctx.setLineDash([]);
                                    ctx.font = '9px Inter, Arial, sans-serif';
                                    ctx.textAlign = 'right';

                                    // Very Low band (0 to index_1)
                                    if (props.statistics.index_1 > -1) {
                                        const midY = yAxis.getPixelForValue(props.statistics.index_1 / 2);
                                        ctx.fillStyle = 'rgb(220, 38, 38)';
                                        ctx.fillText('Very Low', xAxis.right - 8, midY + 3);
                                    }

                                    // Low band (index_1 to index_2)
                                    if (props.statistics.index_1 > -1 && props.statistics.index_2 > -1) {
                                        const midValue = (props.statistics.index_1 + props.statistics.index_2) / 2;
                                        const midY = yAxis.getPixelForValue(midValue);
                                        ctx.fillStyle = 'rgb(245, 158, 11)';
                                        ctx.fillText('Low', xAxis.right - 8, midY + 3);
                                    }

                                    // Normal band (index_2 to index_3)
                                    if (props.statistics.index_2 > -1 && props.statistics.index_3 > -1) {
                                        const midValue = (props.statistics.index_2 + props.statistics.index_3) / 2;
                                        const midY = yAxis.getPixelForValue(midValue);
                                        ctx.fillStyle = 'rgb(34, 197, 94)';
                                        ctx.fillText('Normal', xAxis.right - 8, midY + 3);
                                    }

                                    // High band (index_3 to index_4, or to the top of the chart for a
                                    // lab graded from its target, which has no Very High)
                                    if (props.statistics.index_3 > -1) {
                                        const topValue = props.statistics.index_4 > -1 ? props.statistics.index_4 : yAxis.max;
                                        const midValue = (props.statistics.index_3 + topValue) / 2;
                                        const midY = yAxis.getPixelForValue(midValue);
                                        ctx.fillStyle = 'rgb(21, 128, 61)';
                                        ctx.fillText('High', xAxis.right - 8, midY + 3);
                                    }

                                    // Very High band (index_4 to top of chart)
                                    if (props.statistics.index_4 > -1) {
                                        const chartMax = yAxis.max;
                                        const midValue = (props.statistics.index_4 + chartMax) / 2;
                                        const midY = yAxis.getPixelForValue(midValue);
                                        ctx.fillStyle = 'rgb(15, 78, 37)';
                                        ctx.fillText('Very High', xAxis.right - 8, midY + 3);
                                    }
                                }
                            } else {
                                // For non-indexed charts, show target range
                                const midValue = (props.statistics.minimum + props.statistics.maximum) / 2;
                                const yPos = yAxis.getPixelForValue(midValue);
                                
                                ctx.strokeStyle = '#1B4332';
                                ctx.lineWidth = 1
                                ctx.setLineDash([6, 3]);
                                
                                ctx.beginPath();
                                ctx.moveTo(xAxis.left, yPos);
                                ctx.lineTo(xAxis.right, yPos);
                                ctx.stroke();
                                
                                ctx.setLineDash([]);
                                ctx.font = '8px Inter, Arial, sans-serif';
                                ctx.fillStyle = '#1B4332';
                                ctx.textAlign = 'right';
                                ctx.fillText('Target Range', xAxis.right - 8, yPos - 4);
                            }
                            
                            // Add deviation chart reference lines for pH charts
                            if (props.statistics.chartType === 'ph_water') {
                                // Center line at zero deviation (target pH)
                                const centerPos = yAxis.getPixelForValue(0);
                                ctx.strokeStyle = 'rgb(59, 130, 246)'; // Blue for target line
                                ctx.lineWidth = 2;
                                ctx.setLineDash([]);
                                
                                ctx.beginPath();
                                ctx.moveTo(xAxis.left, centerPos);
                                ctx.lineTo(xAxis.right, centerPos);
                                ctx.stroke();
                                
                                // Add target label
                                ctx.font = '10px Inter, Arial, sans-serif';
                                ctx.fillStyle = 'rgb(59, 130, 246)';
                                ctx.textAlign = 'right';
                                ctx.fillText('Target pH (6.5)', xAxis.right - 8, centerPos - 6);
                            }
                        }
                    }
                ]
            };

            console.log('✅ Chart configuration ready, creating Chart.js instance');
            
            // Double-check canvas is available and clean before creating chart
            if (!canvas) {
                console.error('❌ Canvas element not found, cannot create chart');
                return;
            }
            
            // Final check for any remaining chart instances
            if (canvas.chartInstance) {
                console.warn('⚠️ Found orphaned chart instance on canvas, cleaning up');
                try {
                    canvas.chartInstance.destroy();
                } catch (error) {
                    console.warn('⚠️ Error cleaning orphaned instance:', error);
                }
                delete canvas.chartInstance;
            }
            
            console.log('🎨 Creating new Chart.js instance on clean canvas');
            const chart = new Chart(canvas, config);

            console.log('✅ Chart created successfully with ID:', chart.id);
            
            // Store chart reference in both places for proper cleanup
            chartInstance = chart;
            chart.canvas.chartInstance = chart; // Also store on canvas for cleanup
            
            console.log('📋 Chart instance stored:', {
                chartInstanceId: chartInstance.id,
                canvasHasInstance: !!chart.canvas.chartInstance
            });
            
            // Get the actual container dimensions from the parent
            const container = chart.canvas.parentNode;
            const containerRect = container.getBoundingClientRect();
            
            console.log('📐 Container dimensions at chart creation:', {
                containerWidth: container.offsetWidth,
                containerHeight: container.offsetHeight,
                rectWidth: containerRect.width,
                rectHeight: containerRect.height,
                propsWidth: props.width,
                propsHeight: props.height
            });
            
            // Only set explicit dimensions if they're not percentage values
            if (props.width !== '100%' && props.height !== '100%') {
                console.log('📐 Setting explicit chart dimensions:', {
                    width: props.width,
                    height: props.height
                });
                container.style.width = props.width;
                container.style.height = props.height;
            } else {
                console.log('📐 Setting responsive chart dimensions (100%)');
                // For responsive sizing, ensure the container has proper dimensions first
                container.style.width = '100%';
                container.style.height = '100%';
                
                // Wait a moment for the container to update, then resize the chart
                setTimeout(() => {
                    const updatedRect = container.getBoundingClientRect();
                    console.log('📐 Container dimensions after timeout:', {
                        containerWidth: container.offsetWidth,
                        containerHeight: container.offsetHeight,
                        rectWidth: updatedRect.width,
                        rectHeight: updatedRect.height
                    });
                    
                    if (container.offsetHeight > 0) {
                        console.log('📐 Container ready, calling chart.resize()');
                        try {
                            chart.resize();
                            console.log('📐 Chart resized to:', {
                                canvasWidth: chart.canvas.width,
                                canvasHeight: chart.canvas.height,
                                displayWidth: chart.canvas.style.width,
                                displayHeight: chart.canvas.style.height
                            });
                        } catch (error) {
                            console.warn('⚠️ Error resizing chart:', error);
                        }
                    } else {
                        console.warn('⚠️ Container height still 0, will retry');
                        // If height is still 0, try again after a longer delay
                        setTimeout(() => {
                            if (container.offsetHeight > 0 && chartInstance && !chartInstance.destroyed) {
                                try {
                                    chart.resize();
                                    console.log('📐 Chart resized (retry) to:', {
                                        canvasWidth: chart.canvas.width,
                                        canvasHeight: chart.canvas.height
                                    });
                                } catch (error) {
                                    console.warn('⚠️ Error resizing chart on retry:', error);
                                }
                            }
                        }, 100);
                    }
                }, 10);
            }
        }

        onMounted(() => {
            console.log('🎨 PrintMapChart mounted, waiting for container to be ready...');
            // Wait for Vue to finish calculating and applying container dimensions
            setTimeout(() => {
                const canvas = document.getElementById('map-graph');
                const container = canvas?.parentNode;
                console.log('⏰ Container dimensions check:', {
                    hasCanvas: !!canvas,
                    hasContainer: !!container,
                    containerHeight: container?.offsetHeight || 'N/A',
                    containerWidth: container?.offsetWidth || 'N/A'
                });
                console.log('⏰ Container ready, calling printGraph');
                printGraph();
            }, 100); // Longer delay to ensure DOM and Vue computed properties are ready
        });

        watch(props, (newProps, oldProps) => {
            console.log('🔄 PrintMapChart props changed, destroying and recreating chart');
            console.log('🔄 Props comparison:', {
                hasNewStatistics: !!newProps.statistics,
                hasOldStatistics: !!oldProps.statistics,
                statisticsChanged: newProps.statistics !== oldProps.statistics,
                newWidth: newProps.width,
                newHeight: newProps.height
            });
            
            // Ensure proper cleanup before creating new chart
            if (chartInstance) {
                console.log('🗑️ Destroying chart instance:', chartInstance.id);
                try {
                    chartInstance.destroy();
                } catch (error) {
                    console.warn('⚠️ Error destroying chart instance:', error);
                }
                chartInstance = null;
            }
            
            // Also clean up any chart instance stored on canvas
            const canvas = document.getElementById('map-graph');
            if (canvas && canvas.chartInstance) {
                console.log('🧹 Cleaning up canvas chart instance');
                try {
                    canvas.chartInstance.destroy();
                } catch (error) {
                    console.warn('⚠️ Error destroying canvas chart instance:', error);
                }
                delete canvas.chartInstance;
            }
            
            // Wait a bit to ensure cleanup is complete before creating new chart
            setTimeout(() => {
                console.log('🔄 Creating new chart with updated props');
                printGraph();
            }, 50);
        }, { deep: true, immediate: false })
        
        // Cleanup when component unmounts
        onUnmounted(() => {
            console.log('🧹 Component unmounting, cleaning up chart');
            if (chartInstance) {
                try {
                    chartInstance.destroy();
                } catch (error) {
                    console.warn('⚠️ Error during unmount cleanup:', error);
                }
                chartInstance = null;
            }
            
            const canvas = document.getElementById('map-graph');
            if (canvas && canvas.chartInstance) {
                try {
                    canvas.chartInstance.destroy();
                } catch (error) {
                    console.warn('⚠️ Error cleaning up canvas on unmount:', error);
                }
                delete canvas.chartInstance;
            }
        });

        return { printGraph };
    },
};
</script>
